import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Download, Printer, X } from "lucide-react";
import { buildToken, renderQRLabel, downloadCanvasPNG, printCanvas } from "@/lib/qrLabel";

export default function EquipmentQRDialog({ equipment, siteName, facilityName, onClose }) {
  const canvasRef = useRef(null);
  const [err, setErr] = useState("");
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const lines = [
          (equipment.name || "Equipment").toUpperCase(),
          [siteName, facilityName].filter(Boolean).join("  ·  ") || "—",
          equipment.serial_number ? `S/N: ${equipment.serial_number}` : null,
        ].filter(Boolean);
        await renderQRLabel(canvas, buildToken("eq", equipment.id), lines);
        if (!cancelled) setReady(true);
      } catch (e) {
        if (!cancelled) setErr(String(e.message || e));
      }
    })();
    return () => { cancelled = true; };
  }, [equipment, siteName, facilityName]);

  const download = () => downloadCanvasPNG(canvasRef.current, `QR_${equipment.name || "equipment"}_${(equipment.id || "").slice(0, 6)}`);
  const print = () => printCanvas(canvasRef.current, `QR · ${equipment.name || "equipment"}`);

  return createPortal(
    <div className="vb-modal-bg" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="vb-modal" style={{ maxWidth: 520 }} data-testid="qr-dialog">
        <div className="flex items-center justify-between mb-3">
          <h2 className="m-0">QR label · {equipment.name}</h2>
          <button type="button" onClick={onClose} className="vb-btn ghost" data-testid="qr-close"><X size={16} /></button>
        </div>
        {err && <div className="vb-pill err mb-3">{err}</div>}
        <p className="text-xs opacity-70 mb-3">
          Prints at <strong>8 × 8 cm</strong> (300 DPI). Stick on the equipment — only the in-app scanner can decode it.
        </p>
        <div className="flex justify-center mb-4" style={{ background: "#f5f1ea", padding: 16, borderRadius: 12 }}>
          <canvas
            ref={canvasRef}
            data-testid="qr-canvas"
            style={{ maxWidth: "100%", height: "auto", background: "#fff", boxShadow: "0 1px 4px rgba(0,0,0,0.15)" }}
          />
        </div>
        <div className="flex gap-2 justify-end">
          <button type="button" className="vb-btn ghost" onClick={onClose}>Close</button>
          <button type="button" className="vb-btn ghost" onClick={print} disabled={!ready} data-testid="qr-print">
            <Printer size={16} /> Print
          </button>
          <button type="button" className="vb-btn" onClick={download} disabled={!ready} data-testid="qr-download">
            <Download size={16} /> Download PNG
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
